import type { ChatMessage, Conversation } from "@rmh/defs";

interface ParticipantEntry {
  displayName: string;
  addedAt: string;
}

// Basically, if the map is not a valid JSON object, return an empty object.
// AppSync serialises AWSJSON to a string; a DynamoDB-sourced read hands over
// the parsed map already, so both shapes are accepted.
const parseMap = <T>(raw: unknown): Record<string, T> => {
  if (!raw) {
    return {};
  }
  if (typeof raw === "object") {
    return raw as Record<string, T>;
  }
  try {
    const parsed = JSON.parse(String(raw));
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

/**
 * Everyone who has posted in the thread, in the order they first spoke.
 *
 * Keyed off `senders` rather than `participants`: a colleague who opened the
 * chart but said nothing is a participant, and naming them would imply they
 * had spoken. `participants` only supplies the display name.
 */
export const getSenderNames = (
  conversation?: Conversation | null
): string[] => {
  const senders = parseMap<string>(conversation?.senders);
  const participants = parseMap<ParticipantEntry>(conversation?.participants);

  return Object.keys(senders)
    .sort((a, b) => (senders[a] < senders[b] ? -1 : 1))
    .map((staffId) => participants[staffId]?.displayName)
    .filter((name): name is string => Boolean(name));
};

/**
 * The name to show against a message.
 *
 * `senderDisplayName` only exists on messages written since the shared thread
 * landed, so anything older falls back to the two identities the conversation
 * itself always carries.
 */
export const getSenderLabel = (
  message: Pick<ChatMessage, "senderId" | "senderDisplayName">,
  conversation?: Conversation | null
): string => {
  if (message.senderDisplayName) {
    return message.senderDisplayName;
  }
  if (!conversation) {
    return "";
  }

  const isPatient = message.senderId === conversation.patientId;
  const name = isPatient
    ? [conversation.patientFirstName, conversation.patientLastName]
    : [conversation.providerFirstName, conversation.providerLastName];

  return name.filter(Boolean).join(" ");
};

/**
 * The patient's care team, PT of record first when on it.
 *
 * While the care team is loading, unreadable or empty, falls back to the PT of
 * record plus everyone who has posted, so a patient with no Canvas care team
 * still sees who they are talking to.
 *
 * Matched on display name: the conversation knows the PT by RMH id, the care
 * team by Canvas practitioner id, and nothing client-side joins the two.
 */
export const getCareTeamNames = (
  conversation?: Conversation | null,
  careTeamNames?: string[] | null
): string[] => {
  const ptName = conversation
    ? [conversation.providerFirstName, conversation.providerLastName]
        .filter(Boolean)
        .join(" ")
    : "";

  const careTeam =
    careTeamNames?.filter(
      (name, index, all) => name && all.indexOf(name) === index
    ) ?? [];
  if (!careTeam.length) {
    return [
      ptName,
      ...getSenderNames(conversation).filter((name) => name !== ptName),
    ].filter(Boolean);
  }

  return careTeam.includes(ptName)
    ? [ptName, ...careTeam.filter((name) => name !== ptName)]
    : careTeam;
};
